import { useState } from 'react';

import { MoreHorizontal, LogOut, Trash2, Crown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useResponsive } from '@/hooks/useResponsive';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import type { WorkspaceMemberResponse, WorkspaceRole } from '@optidev/shared-types';

interface MemberRowProps {
  member: WorkspaceMemberResponse;
  currentUserRole: WorkspaceRole | 'owner';
  isCurrentUserOwner: boolean;
  onRoleChange: (memberId: string, newRole: WorkspaceRole) => Promise<void>;
  onRemove: (memberId: string) => Promise<void>;
  onLeave: () => Promise<void>;
}

const roleBadgeColors: Record<string, string> = {
  owner: 'bg-muted text-foreground',
  admin: 'bg-muted text-foreground',
  user: 'bg-muted text-muted-foreground',
  viewer: 'bg-muted text-muted-foreground',
};

/**
 * Member Row Component
 * Displays a single workspace member with role dropdown and actions
 */
export function MemberRow({
  member,
  currentUserRole,
  isCurrentUserOwner,
  onRoleChange,
  onRemove,
  onLeave,
}: MemberRowProps) {
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const { isMobile } = useResponsive();

  const isOwner = member.isOwner;
  const isAdmin = member.role === 'admin';
  const canModify = isCurrentUserOwner || (currentUserRole === 'admin' && !isOwner && !isAdmin);
  const canLeave = member.isCurrentUser && !isOwner;

  // Get display role (owner badge shown separately)
  const displayRole = isOwner ? 'owner' : member.role;

  // Get initials for avatar
  const initials = member.user.displayName
    ? member.user.displayName.charAt(0).toUpperCase()
    : member.user.email.charAt(0).toUpperCase();

  // Format joined date
  const formatJoinedDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Build membership info string
  const getMembershipInfo = () => {
    const parts: string[] = [];
    if (member.joinedAt) {
      parts.push(`Joined ${formatJoinedDate(member.joinedAt)}`);
    }
    if (member.invitedBy) {
      const inviterName = member.invitedBy.displayName || member.invitedBy.email;
      parts.push(`Invited by ${inviterName}`);
    }
    return parts.join(' · ');
  };

  const membershipInfo = getMembershipInfo();

  return (
    <>
      <div className={cn(
        "flex gap-3 py-4",
        isMobile ? "flex-col" : "items-center justify-between"
      )}>
        <div className="flex items-center gap-3 min-w-0">
          {/* Avatar */}
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-foreground/10 text-foreground/70 font-medium text-sm">
            {initials}
          </div>

          {/* Name & Email */}
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-medium truncate">
                {member.user.displayName || member.user.email}
              </span>
              {member.isCurrentUser && (
                <span className="text-xs text-muted-foreground">(you)</span>
              )}
            </div>
            {member.user.displayName && (
              <div className="text-sm text-muted-foreground truncate">
                {member.user.email}
              </div>
            )}
            {membershipInfo && (
              <div className="text-xs text-muted-foreground/60">
                {membershipInfo}
              </div>
            )}
          </div>
        </div>

        <div className={cn(
          "flex items-center gap-3",
          isMobile && "justify-end"
        )}>
          {/* Role Badge/Selector */}
          {canModify && !isOwner ? (
            <Select
              value={member.role}
              onValueChange={(value) => onRoleChange(member.id, value as WorkspaceRole)}
            >
              <SelectTrigger className="w-[110px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <Badge className={roleBadgeColors[displayRole]}>
              {displayRole.charAt(0).toUpperCase() + displayRole.slice(1)}
            </Badge>
          )}

          {/* Actions Menu - only show if there are actual actions */}
          {(canLeave || (canModify && !member.isCurrentUser)) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canLeave && (
                  <DropdownMenuItem
                    onClick={() => setShowLeaveDialog(true)}
                    className="text-destructive"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Leave Workspace
                  </DropdownMenuItem>
                )}
                {canModify && !member.isCurrentUser && (
                  <>
                    {canLeave && <DropdownMenuSeparator />}
                    <DropdownMenuItem
                      onClick={() => setShowRemoveDialog(true)}
                      className="text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Remove Member
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Remove Member Dialog */}
      <AlertDialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove{' '}
              <strong>{member.user.displayName || member.user.email}</strong>{' '}
              from this workspace? They will lose access to all projects.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => onRemove(member.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove Member
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Leave Workspace Dialog */}
      <AlertDialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave Workspace</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to leave this workspace? You will lose
              access to all projects. An admin can invite you back.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onLeave}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Leave Workspace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
